import {
  parseEventConditionToInput,
  toDatePickerInput,
} from '../../../components/Builder/utils/builderUtils';
import {
  IBuilderExpandEvent,
  IBuilderMergeEvent,
  IBuilderSampledCohort,
  ICohortRow,
  LookForwardWindowRequirement,
} from '../../../redux/slices/oldBuilder';
import {
  CohortSampleOperationKey,
  CohortType,
  ExpressionCohortOperatorKey,
  FunnelCohortOperatorKey,
  IBasicCohortInput,
  IBasicCompoundCohortInput,
  IBasicExpressionCohortInput,
  IBasicFunnelCohortInput,
  IBasicMeasureCohortInput,
  IBasicPerformedCohortInput,
  IBasicRetentionCohortInput,
  IBasicSimpleCohortInput,
  ICohortCompareInput,
  ICohortInput,
  ICohortRefInput,
  LogicalOperatorKey,
  Maybe,
  MeasureCohortOperatorKey,
  PerformedCohortOperatorKey,
  RelationalOperatorKey,
  RetentionCohortOperatorKey,
  SimpleCohortTypeKey,
  TimeWindowKey,
  VersionType,
} from '../../apollo/graphqlSchema';
import { assertNotNil } from '../../types';
import { prepareCohortMeasureInput } from '../measures/inputs';
import { MaybeNil } from '../../interfaces';
import {
  parseExpandEvents,
  parseMergeEvents,
} from '../../../components/Builder/utils/validateExecutionInput';
import { parseFilterToInput } from '../../filterFields';
import { zRetentionCohortOperatorKey } from '../../apollo/zodSchema';
import { isNil } from 'lodash';
import { UnreachableCaseError } from 'ts-essentials';

const filterValidNumbers = (numbers?: Array<number | null>): Array<number> => {
  if (!numbers?.length) {
    return [];
  }

  return numbers.reduce<Array<number>>((acc, value) => {
    if (typeof value === 'number') {
      acc.push(value);
    }

    return acc;
  }, []);
};

const parseExpressionCohortInput = (
  cohortRow: ICohortRow,
): IBasicExpressionCohortInput => {
  assertNotNil(cohortRow?.analysisId);
  assertNotNil(cohortRow?.measureId);
  return {
    analysisId: cohortRow.analysisId,
    operator: cohortRow.operator as ExpressionCohortOperatorKey,
    operatorValues: filterValidNumbers(cohortRow.operatorValues),
    percentileFilter: cohortRow.percentileFilter,
    measureId: cohortRow.measureId,
  };
};

const getDurationStartDate = (cohortRow: ICohortRow) => {
  const { datePicker, cohortDurationStart } = cohortRow;
  if (datePicker && cohortDurationStart.required) {
    assertNotNil(cohortDurationStart.date);
    return cohortDurationStart.date;
  } else {
    return null;
  }
};

const getLookForwardWindow = (cohortRow: ICohortRow): Maybe<number> => {
  const { lookForwardWindow, operator } = cohortRow;
  switch (lookForwardWindow.required) {
    case LookForwardWindowRequirement.NotRequired:
      return null;
    case LookForwardWindowRequirement.Required:
      return lookForwardWindow.windowLength ?? null;
    case LookForwardWindowRequirement.OnRetentionOperators:
      const retentionOperator = zRetentionCohortOperatorKey.parse(operator);
      if (lookForwardWindow.operators.includes(retentionOperator)) {
        return lookForwardWindow.windowLength ?? null;
      } else {
        return null;
      }
    default:
      throw new UnreachableCaseError(lookForwardWindow);
  }
};

const getTimeToConvert = (cohortRow: ICohortRow) => {
  const { timeToConvert } = cohortRow;
  if (timeToConvert) {
    return {
      operator: timeToConvert.operator,
      values: filterValidNumbers(timeToConvert.operatorValues),
      timeUnit: timeToConvert.timeUnit,
    };
  } else {
    return null;
  }
};

const parseFunnelCohortInput = (
  cohortRow: ICohortRow,
): IBasicFunnelCohortInput => {
  assertNotNil(cohortRow?.analysisId);

  return {
    analysisId: cohortRow.analysisId,
    operator: cohortRow.operator as FunnelCohortOperatorKey,
    operatorValues: filterValidNumbers(cohortRow.operatorValues),
    lookForwardWindow: getLookForwardWindow(cohortRow),
    durationStartDate: getDurationStartDate(cohortRow),
    timeToConvert: getTimeToConvert(cohortRow),
  };
};

const parseMeasureCohortInput = (
  cohortRow: ICohortRow,
  schemaId: string,
): IBasicMeasureCohortInput => ({
  operator: cohortRow.operator as MeasureCohortOperatorKey,
  operatorValues: filterValidNumbers(cohortRow.operatorValues),
  measure: prepareCohortMeasureInput(cohortRow, schemaId),
  percentileFilter: cohortRow.percentileFilter,
});

const parsePerformedCohortInput = (
  cohortRow: ICohortRow,
  schemaId: string,
): IBasicPerformedCohortInput => ({
  operator: cohortRow.operator as PerformedCohortOperatorKey,
  operatorValues: filterValidNumbers(cohortRow.operatorValues),
  eventConditions: cohortRow.eventConditions.map((eventCondition) =>
    parseEventConditionToInput(eventCondition, schemaId),
  ),
});

const parseRetentionCohortInput = (
  cohortRow: ICohortRow,
): IBasicRetentionCohortInput => {
  assertNotNil(cohortRow?.analysisId);

  const operator = zRetentionCohortOperatorKey.parse(cohortRow.operator);
  return {
    analysisId: cohortRow.analysisId,
    operator,
    operatorValues: filterValidNumbers(cohortRow.operatorValues),
    returnedOperator: cohortRow?.returnedOperator
      ? (cohortRow.returnedOperator as RelationalOperatorKey)
      : null,
    lookForwardWindow: getLookForwardWindow(cohortRow),
    durationStartDate: getDurationStartDate(cohortRow),
  };
};

export const prepareBasicSimpleCohortInput = (
  cohortRow: ICohortRow,
  schemaId: string,
): IBasicSimpleCohortInput => {
  const {
    simpleCohortTypeKey,
    conditionFilter,
    subject,
    timeWindow,
    timeWindowValues,
    datePicker,
  } = cohortRow;
  assertNotNil(subject);
  const result: IBasicSimpleCohortInput = {
    subject,
    type: simpleCohortTypeKey,
    filter: conditionFilter
      ? parseFilterToInput(conditionFilter, schemaId)
      : null,
    timeWindow: (timeWindow as TimeWindowKey) || null,
    timeWindowValues: filterValidNumbers(timeWindowValues),
    expressionCohort: null,
    funnelCohort: null,
    measureCohort: null,
    performedCohort: null,
    retentionCohort: null,
    datePicker: !isNil(datePicker) ? toDatePickerInput(datePicker) : null,
  };

  if (simpleCohortTypeKey === SimpleCohortTypeKey.Expression) {
    result.expressionCohort = parseExpressionCohortInput(cohortRow);
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Funnel) {
    result.funnelCohort = parseFunnelCohortInput(cohortRow);
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Measure) {
    result.measureCohort = parseMeasureCohortInput(cohortRow, schemaId);
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Performed) {
    result.performedCohort = parsePerformedCohortInput(cohortRow, schemaId);
  } else if (simpleCohortTypeKey === SimpleCohortTypeKey.Retention) {
    result.retentionCohort = parseRetentionCohortInput(cohortRow);

    const operator = cohortRow.operator as RetentionCohortOperatorKey;

    if (
      operator === RetentionCohortOperatorKey.Retained ||
      operator === RetentionCohortOperatorKey.NotRetained
    ) {
      result.retentionCohort.operatorValues = [];
      result.retentionCohort.returnedOperator = null;
      result.timeWindow = TimeWindowKey.Overall;
      result.timeWindowValues = [];
    }

    if (operator !== RetentionCohortOperatorKey.Returned) {
      result.retentionCohort.returnedOperator = null;
    }
  } else {
    throw new UnreachableCaseError(simpleCohortTypeKey);
  }

  return result;
};

export const prepareBasicCompoundCohortInput = (
  cohortRows: ICohortRow[],
  logicOp: LogicalOperatorKey,
  schemaId: string,
): IBasicCompoundCohortInput => ({
  logicOps: cohortRows.map((_) => logicOp),
  simpleCohorts: cohortRows.map((cohortRow) => {
    if (cohortRow.cohortId) {
      return {
        basicSimpleCohort: null,
        namedSimpleCohort: {
          id: cohortRow.cohortId,
        },
        versionType: VersionType.Named,
      };
    } else {
      return {
        basicSimpleCohort: prepareBasicSimpleCohortInput(cohortRow, schemaId),
        namedSimpleCohort: null,
        versionType: VersionType.Basic,
      };
    }
  }),
});

type IPrepareCohortInputArgs =
  | {
      type: CohortType.Simple;
      cohortRow: ICohortRow;
      schemaId: string;
    }
  | {
      type: CohortType.Compound;
      id: string;
      cohortRows: ICohortRow[];
      logicOpKey: LogicalOperatorKey;
      schemaId: string;
    };

export const prepareBasicCohortInput = (
  args: IPrepareCohortInputArgs,
): IBasicCohortInput => {
  const { schemaId } = args;
  if (args.type === CohortType.Simple) {
    const { type, cohortRow } = args;
    return {
      compoundCohort: null,
      simpleCohort: prepareBasicSimpleCohortInput(cohortRow, schemaId),
      type,
    };
  } else {
    const { type, cohortRows, logicOpKey } = args;

    return {
      compoundCohort: prepareBasicCompoundCohortInput(
        cohortRows,
        logicOpKey,
        schemaId,
      ),
      simpleCohort: null,
      type,
    };
  }
};

export const parseCohortRowToCohortInput = (
  args: IPrepareCohortInputArgs,
): ICohortInput => {
  if (args.type === CohortType.Simple) {
    const { cohortRow } = args;
    const { cohortId } = cohortRow;

    const versionType = cohortId ? VersionType.Named : VersionType.Basic;

    // named cohort
    if (cohortId) {
      return {
        basicCohort: null,
        namedCohort: {
          id: cohortId,
        },
        versionType,
      };
    }

    return {
      basicCohort: prepareBasicCohortInput(args),
      namedCohort: null,
      versionType,
    };
  } else {
    const { id } = args;

    const versionType = id ? VersionType.Named : VersionType.Basic;

    // named cohort
    if (id) {
      return {
        basicCohort: null,
        namedCohort: {
          id,
        },
        versionType,
      };
    }

    return {
      basicCohort: prepareBasicCohortInput(args),
      namedCohort: null,
      versionType,
    };
  }
};

export const parseCohortRowsToCohortRefInput = (
  cohortRows: ICohortRow[],
  schemaId: string,
): ICohortRefInput[] => {
  if (!cohortRows?.length) {
    return [];
  }

  const activeCohorts = cohortRows.filter((cohortRow) => cohortRow.isActive);

  if (!activeCohorts?.length) {
    return [];
  }

  return activeCohorts.map((cohortRow) => {
    const { contextInput, datePicker } = cohortRow;

    return {
      cohort: parseCohortRowToCohortInput({
        type: CohortType.Simple,
        cohortRow,
        schemaId,
      }),
      props: {
        samplePercentage: contextInput.samplePercentage,
        datePicker: !isNil(datePicker) ? toDatePickerInput(datePicker) : null,
      },
    };
  });
};

export const prepareCohortSampleCohortInput = (
  cohortRows: ICohortRow[],
  builderSampledCohort: IBuilderSampledCohort,
  schemaId: string,
): ICohortInput => {
  const { id, isCompoundCohort, sampleOperation } = builderSampledCohort;

  if (isCompoundCohort && sampleOperation) {
    return parseCohortRowToCohortInput({
      type: CohortType.Compound,
      id,
      cohortRows,
      logicOpKey: sampleOperation.logicOp.key,
      schemaId,
    });
  }

  const cohortRow = cohortRows[0];

  const simpleCohortRow = {
    ...cohortRow,
  };

  return parseCohortRowToCohortInput({
    type: CohortType.Simple,
    cohortRow: simpleCohortRow,
    schemaId,
  });
};

export const prepareCohortCompareInput = (
  sampleOperationKey: MaybeNil<CohortSampleOperationKey>,
  expandEvents: IBuilderExpandEvent[],
  mergeEvents: IBuilderMergeEvent[],
  schemaId: string,
): ICohortCompareInput | null => {
  if (sampleOperationKey !== CohortSampleOperationKey.Compare) {
    return null;
  }

  return {
    expandEvents: parseExpandEvents(expandEvents, schemaId),
    mergeEvents: parseMergeEvents(mergeEvents, schemaId),
  };
};
